import { defineComponent, ref, h, onUnmounted, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import {
    VDialog,
    VCard,
    VCardTitle,
    VCardText,
    VCardActions,
    VBtn,
} from 'vuetify/components'

export interface InactivityController {
    show: (duration?: number, onTimeout?: () => void, onStayLoggedIn?: () => void) => void
    hide: () => void
    component: ReturnType<typeof defineComponent>
}

/**
 * Crée un composant contrôlable depuis l’extérieur (const inactivityDialog = createInactivityDialog();).
 * Doit être enregistré globalement dans l’application pour être utilisé. (ex: app.component('InactivityDialog', inactivityDialog.component))
 * Doit être également mise dans le template de l’application (sous v-app).
 */
export function createInactivityDialog(): InactivityController {
    const isVisible = ref(false)
    const countdown = ref(0)
    let timer: ReturnType<typeof setInterval> | null = null
    let onTimeoutCallback: (() => void) | null = null
    let onStayLoggedInCallback: (() => void) | null = null

    const clearTimer = () => {
        if (timer) {
            clearInterval(timer)
            timer = null
        }
    }

    const hide = () => {
        isVisible.value = false
        clearTimer()
    }

    const show = (duration = 15, onTimeout?: () => void, onStayLoggedIn?: () => void) => {
        countdown.value = duration
        onTimeoutCallback = onTimeout ?? null
        onStayLoggedInCallback = onStayLoggedIn ?? null
        isVisible.value = true

        clearTimer()
        timer = setInterval(() => {
            countdown.value--
            if (countdown.value <= 0) {
                clearTimer()
                countdown.value = 0
                isVisible.value = false
                if (onTimeoutCallback) onTimeoutCallback()
            }
        }, 1000)
    }

    const onOk = () => {
        hide()
        if (onStayLoggedInCallback) onStayLoggedInCallback()
    }

    const component = defineComponent({
        name: 'InactivityDialog',
        setup() {
            const { t } = useI18n();
            onUnmounted(clearTimer)

            return () =>
                h(
                    VDialog,
                    {
                        modelValue: isVisible.value,
                        'onUpdate:modelValue': (v: boolean) => (isVisible.value = v),
                        maxWidth: 400,
                        persistent: true, // 🔒 empêche la fermeture par clic extérieur
                    },
                    {
                        default: () =>
                            h(VCard, null, {
                                default: () => [
                                    h(VCardTitle, null, { default: () => t('inactivity.title') }),
                                    h(VCardText, null, {
                                        default: () => t('inactivity.message', { seconds: countdown.value }),
                                    }),
                                    h(VCardActions, { class: 'justify-end' }, {
                                        default: () => [
                                            h(
                                                VBtn,
                                                { color: 'yellow', onClick: onOk },
                                                { default: () => t('inactivity.action') }
                                            ),
                                        ],
                                    }),
                                ],
                            }),
                    }
                )
        },
    })

    return { show, hide, component }
}
